import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PasswordPhaseBody } from './password-phase-body';
import { startStepUpAuth } from '../../api';
import style from '../../style.module.css';

/**
 * @typedef {import('../../api').StartStepUpAuthRes} StartStepUpAuthRes
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */

/**
 * @param {object} props
 * @param {Record<string, MessageDescriptor>} props.messages
 * @param {() => void} props.onCancel
 * @param {(arg0: StartStepUpAuthRes) => void} props.onPasswordVerify
 * @param {boolean} props.isMfaSettings
 * @param {boolean} [props.isUnskippable]
 * @param {string} [props.phoneVerificationState]
 * @returns {React.JSX.Element}
 */
export const PasswordPhaseModalContent = ({
  messages,
  onCancel,
  onPasswordVerify,
  isMfaSettings,
  isUnskippable = false,
  phoneVerificationState,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isDisabled, setIsDisabled] = useAsyncSafeState(false);

  const onSubmit = useCallback(
    async (/** * @type {React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>} */ e) => {
      e.preventDefault();
      try {
        setIsDisabled(true);
        const resData = await startStepUpAuth({ password, isMfaSettings, tryWithoutPassword: false });
        onPasswordVerify(resData);
      } catch (err) {
        setError(err);
      } finally {
        setIsDisabled(false);
      }
    },
    [onPasswordVerify, password, setIsDisabled, isMfaSettings],
  );

  return (
    <>
      <ModalBody className={style.body}>
        <PasswordPhaseBody
          error={error}
          isDisabled={isDisabled}
          isUnskippable={isUnskippable}
          messages={messages}
          onSubmit={onSubmit}
          password={password}
          phoneVerificationState={phoneVerificationState}
          setError={setError}
          setPassword={setPassword}
        />
      </ModalBody>
      <ModalFooter>
        <FlexRow className={style['footer-buttons']} gap={Gap.Space16}>
          {!isUnskippable && (
            <Button disabled={isDisabled} onClick={onCancel} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
          )}
          <Button disabled={isDisabled} onClick={onSubmit} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.submitButton} />
          </Button>
        </FlexRow>
      </ModalFooter>
    </>
  );
};
